import {
  commandRequest,
  type UiCommandRequest,
} from '../../../../agents/context/ui-command-registry.js';
import { useTranslation } from '@govard/i18n-core/react';
import type { ReactElement } from 'react';
import { bulkApprovalItems } from '../../../approvals/lib/bulkApprovalSummary.js';
import { formatDateTime } from '../../../../shared/format/index.js';
import {
  summarizeApproval,
  summarizeApprovalPresentation,
} from '../../../approvals/lib/approvalSummary.js';

import { AiApprovalCard } from '../AiApprovalCard.js';
import type {
  ApprovalExecutionOutcome,
  ApprovalExecutionStatus,
  ApprovalBulkExecutionAggregate,
  ApprovalItem,
  ApprovalExecutionResult,
  ApprovalStatus,
} from '../../../approvals/api/approvals.client.js';
import {
  isApprovalPresentationV1,
  parseApprovalConsistencyWarnings,
} from '../../../approvals/api/approvals.client.js';
import { AddDealNoteConfirmationSnippet } from './AddDealNoteConfirmationSnippet.js';
import { ChatFileAttachmentSnippet } from './ChatFileAttachmentSnippet.js';
import { ChatFilePreviewSnippet, type ChatFilePreviewV1 } from './ChatFilePreviewSnippet.js';
import { ContactSnippet } from './ContactSnippet.js';
import { DealSnippet } from './DealSnippet.js';
import { DealCockpitSnippet } from './DealCockpitSnippet.js';
import { DealCallPreparationSnippet } from './DealCallPreparationSnippet.js';
import { DealActionPlanSnippet } from './DealActionPlanSnippet.js';
import { DealRiskSnippet } from './DealRiskSnippet.js';
import { DealSummarySnippet } from './DealSummarySnippet.js';
import { EmailDraftSnippet } from './EmailDraftSnippet.js';
import { MemorySnippet } from './MemorySnippet.js';
import { ManagerBriefingSnippet } from './ManagerBriefingSnippet.js';
import { MeetingOutcomeSnippet } from './MeetingOutcomeSnippet.js';
import { SalesHealthSnippet } from './SalesHealthSnippet.js';
import { ScenarioSnippet } from './ScenarioSnippet.js';
import {
  TouchStrategySnippet,
  type TouchStrategyFailureCode,
  type TouchStrategyPayload,
} from './TouchStrategySnippet.js';
import {
  QualificationProposalSnippet,
  type QualificationFailureCode,
  type QualificationProposalPayload,
} from './QualificationProposalSnippet.js';
import { ViewSnippet } from './ViewSnippet.js';
import { ArtifactSnippet, extractArtifactFromMarkdown } from './ArtifactSnippet.js';
import type { ArtifactContent } from '../ArtifactCanvas.js';
import { CrmActionPlanCard, type CrmActionPlanCallbacks } from '../CrmActionPlanCard.js';
import { CrmStructuredList } from './CrmStructuredList.js';
import { RESULT_ACTION_COMMAND_IDS } from '../../../../agents/context/result-action-commands.js';

import type { ChatContextRef, Message, StructuredMessagePayload } from '@govard/ai-chat-kit';

interface StructuredMessageRendererProps extends CrmActionPlanCallbacks {
  message: Message;
  artifactDealId?: string | undefined;
  onSendEmailDraft?:
    | ((draft: Extract<StructuredMessagePayload, { type: 'email_draft' }>['draft']) => void)
    | undefined;
  sendEmailDraftDisabled?: boolean;
  onPrepareDealFollowUp?: ((dealId: string) => void) | undefined;
  prepareDealFollowUpDisabled?: boolean;
  onProposeTouchStrategy?: ((dealId: string) => void) | undefined;
  onCreateDealTasks?:
    | ((
        plan: Extract<StructuredMessagePayload, { type: 'deal_action_plan' }>['plan'],
        actions: Extract<StructuredMessagePayload, { type: 'deal_action_plan' }>['plan']['actions'],
      ) => void)
    | undefined;
  createDealTasksDisabled?: boolean;
  onCreateMeetingTasks?:
    | ((
        meeting: Extract<StructuredMessagePayload, { type: 'meeting_outcome' }>['meeting'],
        actions: readonly { title: string; rationale?: string | null }[],
      ) => void)
    | undefined;
  createMeetingTasksDisabled?: boolean;
  onPrepareMeetingFollowUp?:
    | ((meeting: Extract<StructuredMessagePayload, { type: 'meeting_outcome' }>['meeting']) => void)
    | undefined;
  prepareMeetingFollowUpDisabled?: boolean;
  onOpenCanvas?: ((artifact: ArtifactContent) => void) | undefined;
  onOpenEntity?: ((type: 'deal' | 'contact', id: string, title?: string) => void) | undefined;
  onExecutePrompt?: ((prompt: string) => void) | undefined;
  onExecuteScenario?:
    | ((
        request:
          | string
          | { prompt: string; command?: UiCommandRequest; contextRefs?: ChatContextRef[] },
      ) => void)
    | undefined;
  onRequestModelUpgrade?:
    | ((offer: Extract<StructuredMessagePayload, { type: 'model_upgrade_offer' }>) => void)
    | undefined;
  onApprovalDecisionApplied?: ((approval: ApprovalItem) => void) | undefined;
  compact?: boolean | undefined;
}

const ACTION_PLAN_STATUSES = new Set([
  'blocked',
  'pending',
  'approved',
  'rejected',
  'succeeded',
  'failed',
  'cancelled',
  'undo_pending',
  'undone',
  'stale',
]);

const ACTION_PLAN_ENTITY_TYPES = new Set([
  'company',
  'contact',
  'deal',
  'task',
  'note',
  'calendar_event',
  'contact_interaction',
]);

function isCrmActionPlanPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'crm_action_plan_v1' }> {
  if (payload.type !== 'crm_action_plan_v1' || !isRecord(payload.plan)) return false;
  if (typeof payload.plan.id !== 'string' || !Array.isArray(payload.plan.items)) return false;
  const itemIds = new Set<string>();
  for (const item of payload.plan.items) {
    if (
      !isRecord(item) ||
      typeof item.id !== 'string' ||
      itemIds.has(item.id) ||
      typeof item.ordinal !== 'number' ||
      !Number.isSafeInteger(item.ordinal) ||
      item.ordinal < 0 ||
      typeof item.action !== 'string' ||
      !isRecord(item.entity) ||
      typeof item.entity.type !== 'string' ||
      !ACTION_PLAN_ENTITY_TYPES.has(item.entity.type) ||
      typeof item.entity.label !== 'string' ||
      !Array.isArray(item.fields) ||
      typeof item.status !== 'string' ||
      !ACTION_PLAN_STATUSES.has(item.status)
    ) {
      return false;
    }
    if (
      item.fields.some(
        (field) =>
          !isRecord(field) ||
          typeof field.label !== 'string' ||
          (!['string', 'number', 'boolean'].includes(typeof field.value) && field.value !== null),
      )
    ) {
      return false;
    }
    if (item.approvalId !== undefined && typeof item.approvalId !== 'string') return false;
    if (item.dependsOnItemId !== undefined && typeof item.dependsOnItemId !== 'string')
      return false;
    if (item.canUndo !== undefined && typeof item.canUndo !== 'boolean') return false;
    itemIds.add(item.id);
  }
  return true;
}

function isCrmListPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'crm_list_v1' }> {
  if (payload.type !== 'crm_list_v1' || !isRecord(payload.list)) return false;
  const list = payload.list as Record<string, unknown>;
  return (
    typeof list.entityType === 'string' &&
    typeof list.total === 'number' &&
    Number.isInteger(list.total) &&
    (list.total as number) >= 0 &&
    typeof list.hasMore === 'boolean' &&
    (list.nextCursor === null || typeof list.nextCursor === 'string') &&
    Array.isArray(list.appliedFilters) &&
    Array.isArray(list.rows) &&
    list.rows.length <= 50 &&
    list.rows.every(
      (row) =>
        isRecord(row) &&
        typeof row.id === 'string' &&
        typeof row.label === 'string' &&
        (row.url === null || typeof row.url === 'string') &&
        Array.isArray(row.fields),
    )
  );
}

/**
 * PR 5: provenance payload persisted on the user message a conversation file
 * was attached to. Display-safe by contract: file name + format (the content
 * hash is never rendered).
 */
function isChatFileAttachmentPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'chat_file_attachment_v1' }> {
  return (
    payload.type === 'chat_file_attachment_v1' &&
    typeof payload.attachmentId === 'string' &&
    typeof payload.fileName === 'string' &&
    typeof payload.format === 'string' &&
    typeof payload.contentHash === 'string'
  );
}

/**
 * PR 5: runtime guard for the XLSX preview payload. It is a flow-level
 * companion type (not part of the persisted kit union), so the check works
 * structurally over the runtime value.
 */
function isChatFilePreviewPayload(value: unknown): value is ChatFilePreviewV1 {
  const record = value;
  if (!isRecord(record) || record.type !== 'chat_file_preview_v1') return false;
  if (
    typeof record.attachmentId !== 'string' ||
    typeof record.fileName !== 'string' ||
    typeof record.format !== 'string' ||
    typeof record.rows !== 'number' ||
    !Number.isSafeInteger(record.rows) ||
    record.rows < 0 ||
    !Array.isArray(record.headers) ||
    !record.headers.every((header) => typeof header === 'string')
  ) {
    return false;
  }
  if (!isRecord(record.mapping)) return false;
  const mapping = record.mapping as Record<string, unknown>;
  if (!Object.values(mapping).every((field) => typeof field === 'string')) return false;
  if (!Array.isArray(record.sampleRows)) return false;
  return record.sampleRows.every(
    (row) => Array.isArray(row) && row.every((cell) => typeof cell === 'string'),
  );
}

interface ApprovalCardEntry {
  approval: ApprovalItem;
  summary: string;
  diff: string;
}

type Translate = ReturnType<typeof useTranslation>['t'];

const APPROVAL_SUMMARY_KEYS: Readonly<Record<string, string>> = {
  create_deal: 'crm.approvals.summary.create_deal',
  update_deal: 'crm.approvals.summary.update_deal',
  delete_deal: 'crm.approvals.summary.delete_deal',
  add_deal_note: 'crm.approvals.summary.add_deal_note',
  update_deal_stage: 'crm.approvals.summary.update_deal_stage',
  create_deal_tasks: 'crm.approvals.summary.create_deal_tasks',
  send_email: 'crm.approvals.summary.send_email',
  enrich_contact: 'crm.approvals.summary.enrich_contact',
  run_scenario: 'crm.approvals.summary.run_scenario',
  write_memory: 'crm.approvals.summary.write_memory',
  create_task: 'crm.approvals.summary.create_task',
  update_task: 'crm.approvals.summary.update_task',
  delete_task: 'crm.approvals.summary.delete_task',
  set_task_status: 'crm.approvals.summary.set_task_status',
  assign_task: 'crm.approvals.summary.assign_task',
  link_task: 'crm.approvals.summary.link_task',
  unlink_task: 'crm.approvals.summary.unlink_task',
  set_task_due_date: 'crm.approvals.summary.set_task_due_date',
  create_note: 'crm.approvals.summary.create_note',
  update_note: 'crm.approvals.summary.update_note',
  delete_note: 'crm.approvals.summary.delete_note',
  link_note: 'crm.approvals.summary.link_note',
  unlink_note: 'crm.approvals.summary.unlink_note',
  bulk_create_deals: 'crm.approvals.bulk.summary.create_deals',
  bulk_update_deals: 'crm.approvals.bulk.summary.update_deals',
  bulk_delete_deals: 'crm.approvals.bulk.summary.delete_deals',
  bulk_set_deal_stage: 'crm.approvals.bulk.summary.set_deal_stage',
  bulk_set_deal_owner: 'crm.approvals.bulk.summary.set_deal_owner',
  bulk_set_deal_tags: 'crm.approvals.bulk.summary.set_deal_tags',
  bulk_create_tasks: 'crm.approvals.bulk.summary.create_tasks',
  bulk_update_tasks: 'crm.approvals.bulk.summary.update_tasks',
  bulk_delete_tasks: 'crm.approvals.bulk.summary.delete_tasks',
  bulk_set_task_status: 'crm.approvals.bulk.summary.set_task_status',
  bulk_set_task_assignee: 'crm.approvals.bulk.summary.set_task_assignee',
  bulk_set_task_due_date: 'crm.approvals.bulk.summary.set_task_due_date',
  bulk_create_notes: 'crm.approvals.bulk.summary.create_notes',
  bulk_update_notes: 'crm.approvals.bulk.summary.update_notes',
  bulk_delete_notes: 'crm.approvals.bulk.summary.delete_notes',
  bulk_link_notes: 'crm.approvals.bulk.summary.link_notes',
  bulk_unlink_notes: 'crm.approvals.bulk.summary.unlink_notes',
};

function safeDetailValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const normalized = Array.from(value)
      .map((character) => {
        const codePoint = character.codePointAt(0) ?? 0;
        return codePoint <= 0x1f || codePoint === 0x7f ? ' ' : character;
      })
      .join('')
      .replace(/\s+/g, ' ')
      .trim();
    if (normalized === '') return null;
    return Array.from(normalized).slice(0, 160).join('');
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function valueAtPath(record: Readonly<Record<string, unknown>>, path: readonly string[]): unknown {
  let value: unknown = record;
  for (const segment of path) {
    if (!isRecord(value)) return undefined;
    value = value[segment];
  }
  return value;
}

function canonicalApprovalCopy(
  toolName: string,
  args: Readonly<Record<string, unknown>>,
  t: Translate,
): { summary: string; diff: string } {
  const summaryKey = APPROVAL_SUMMARY_KEYS[toolName];
  const canonicalSummary = summarizeApproval({
    id: 'approval-preview',
    kind: 'tool_call',
    status: 'pending',
    payload: { toolName, args },
    expiresAt: new Date(0).toISOString(),
  });
  const summary =
    summaryKey === undefined ? canonicalSummary.action : t(summaryKey as Parameters<Translate>[0]);
  if (toolName.startsWith('bulk_')) {
    const count = Math.min(bulkApprovalItems(toolName, args).length, 100);
    const values = isRecord(args['values']) ? args['values'] : {};
    const changes: string[] = [];
    for (const [key, labelKey] of [
      ['title', 'crm.approvals.detail.title'],
      ['amount', 'crm.approvals.bulk.field.amount'],
      ['stage', 'crm.approvals.detail.stage'],
      ['priority', 'crm.approvals.detail.priority'],
    ] as const) {
      const value = safeDetailValue(values[key]);
      if (value !== null) changes.push(`${t(labelKey)}: ${value}`);
    }
    const status = safeDetailValue(args['status']);
    const dueAt = safeDetailValue(args['dueAt']);
    if (status !== null) changes.push(`${t('crm.approvals.detail.status')}: ${status}`);
    if (dueAt !== null) changes.push(`${t('crm.approvals.detail.due_at')}: ${dueAt}`);
    if ('assigneeMemberId' in args) {
      changes.push(
        args['assigneeMemberId'] === null
          ? t('crm.approvals.bulk.assignee.remove')
          : t('crm.approvals.bulk.assignee.change'),
      );
    }
    return {
      summary,
      diff: [t('crm.approvals.bulk.review_count', { count }), ...changes].join(' · '),
    };
  }
  if (toolName === 'update_deal_stage') {
    const dealTitle = safeDetailValue(args['dealTitle']);
    const fromStage = safeDetailValue(args['fromStage']);
    const toStage = safeDetailValue(args['toStage']);
    const details = [
      dealTitle,
      fromStage === null || toStage === null
        ? null
        : t('crm.approvals.detail.stage_transition', {
            fromStage,
            toStage,
          }),
    ].filter((value): value is string => value !== null);
    return {
      summary,
      diff: details.length > 0 ? details.join(' · ') : t('crm.approvals.detail.review'),
    };
  }
  const isTaskOrNoteWrite = [
    'create_task',
    'update_task',
    'delete_task',
    'set_task_status',
    'assign_task',
    'link_task',
    'unlink_task',
    'set_task_due_date',
    'create_note',
    'update_note',
    'delete_note',
    'link_note',
    'unlink_note',
  ].includes(toolName);
  const detailSpecs: ReadonlyArray<{
    path: readonly string[];
    labelKey: Parameters<Translate>[0];
  }> = [
    { path: ['title'], labelKey: 'crm.approvals.detail.title' },
    { path: ['name'], labelKey: 'crm.approvals.detail.name' },
    { path: ['firstName'], labelKey: 'crm.approvals.detail.first_name' },
    { path: ['lastName'], labelKey: 'crm.approvals.detail.last_name' },
    { path: ['phone'], labelKey: 'crm.approvals.detail.phone' },
    { path: ['email'], labelKey: 'crm.approvals.detail.email' },
    { path: ['role'], labelKey: 'crm.approvals.detail.role' },
    { path: ['dealTitle'], labelKey: 'crm.approvals.detail.deal' },
    { path: ['toStage'], labelKey: 'crm.approvals.detail.stage' },
    { path: ['subject'], labelKey: 'crm.approvals.detail.subject' },
    { path: ['toEmail'], labelKey: 'crm.approvals.detail.recipient' },
    { path: ['key'], labelKey: 'crm.approvals.detail.memory' },
    { path: ['scenarioId'], labelKey: 'crm.approvals.detail.scenario' },
    ...(!isTaskOrNoteWrite
      ? [
          {
            path: ['contactId'],
            labelKey: 'crm.approvals.detail.contact' as Parameters<Translate>[0],
          },
          { path: ['dealId'], labelKey: 'crm.approvals.detail.deal' as Parameters<Translate>[0] },
          {
            path: ['companyId'],
            labelKey: 'crm.approvals.detail.company' as Parameters<Translate>[0],
          },
          {
            path: ['context', 'dealId'],
            labelKey: 'crm.approvals.detail.deal' as Parameters<Translate>[0],
          },
          {
            path: ['context', 'contactId'],
            labelKey: 'crm.approvals.detail.contact' as Parameters<Translate>[0],
          },
        ]
      : []),
    ...(isTaskOrNoteWrite
      ? [
          { path: ['status'], labelKey: 'crm.approvals.detail.status' as Parameters<Translate>[0] },
          {
            path: ['priority'],
            labelKey: 'crm.approvals.detail.priority' as Parameters<Translate>[0],
          },
          { path: ['dueAt'], labelKey: 'crm.approvals.detail.due_at' as Parameters<Translate>[0] },
        ]
      : []),
  ];
  const details: string[] = [];
  for (const spec of detailSpecs) {
    const value = safeDetailValue(valueAtPath(args, spec.path));
    if (value !== null)
      details.push(`${t(spec.labelKey)}: ${humanizeApprovalDetail(spec.path, value, t)}`);
  }
  const actions = args['actions'];
  if (Array.isArray(actions)) {
    details.push(t('crm.approvals.detail.actions', { count: actions.length }));
  }
  return {
    summary,
    diff: details.length > 0 ? details.join(' · ') : t('crm.approvals.detail.review'),
  };
}

/**
 * Raw tool values ("high", "2026-09-21T09:30:00+03:00") must not reach the
 * confirmation card: the user confirms a human-readable change, not a payload.
 */
function humanizeApprovalDetail(path: readonly string[], value: string, t: Translate): string {
  const key = path[path.length - 1];
  if (key === 'priority') {
    const normalized = value.trim().toLowerCase();
    const labelKey = `crm.chat.action_plan.priority.${normalized}` as Parameters<Translate>[0];
    const label = t(labelKey);
    return label === labelKey ? value : label;
  }
  if (key === 'dueAt' || key === 'dueDate' || key === 'startDate' || key === 'nextActivityDue') {
    const formatted = formatDateTime(value);
    return formatted === '—' ? value : formatted;
  }
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonRecord(content: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(content);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

const EXECUTION_STATUSES: ReadonlySet<ApprovalExecutionStatus> = new Set([
  'requested',
  'awaiting_approval',
  'approved',
  'rejected',
  'expired',
  'cancelled',
  'executing',
  'succeeded',
  'failed',
  'not_executed',
]);

function parseApprovalStatus(value: unknown): ApprovalStatus | undefined {
  return typeof value === 'string' &&
    ['pending', 'approved', 'rejected', 'expired', 'cancelled'].includes(value)
    ? (value as ApprovalStatus)
    : undefined;
}

function parseExecutionOutcome(value: unknown): ApprovalExecutionOutcome | undefined {
  if (!isRecord(value) || typeof value.status !== 'string') return undefined;
  if (!EXECUTION_STATUSES.has(value.status as ApprovalExecutionStatus)) return undefined;
  const attempt = value.attempt;
  if (
    attempt !== null &&
    (typeof attempt !== 'number' || !Number.isSafeInteger(attempt) || attempt < 0)
  ) {
    return undefined;
  }
  const errorCode =
    typeof value.errorCode === 'string' && /^[a-z][a-z0-9_]{0,99}$/.test(value.errorCode)
      ? value.errorCode
      : null;
  return {
    status: value.status as ApprovalExecutionStatus,
    errorCode,
    attempt,
  };
}

function parseBulkExecutionAggregate(value: unknown): ApprovalBulkExecutionAggregate | null {
  if (!isRecord(value) || value.version !== 'partial_durable_v1') return null;
  if (value.entity !== 'deal' && value.entity !== 'task' && value.entity !== 'note') return null;
  const counts = ['total', 'succeeded', 'failed', 'stale', 'skipped', 'unfinished'] as const;
  if (
    counts.some(
      (key) =>
        typeof value[key] !== 'number' ||
        !Number.isSafeInteger(value[key]) ||
        (value[key] as number) < 0 ||
        (value[key] as number) > 100,
    ) ||
    !Array.isArray(value.sample) ||
    value.sample.length > 20
  )
    return null;
  const total = value.total as number;
  const succeeded = value.succeeded as number;
  const failed = value.failed as number;
  const stale = value.stale as number;
  const skipped = value.skipped as number;
  const unfinished = value.unfinished as number;
  if (succeeded + failed + stale + skipped + unfinished !== total) return null;
  if (value.sample.length > total) return null;
  const sample: ApprovalBulkExecutionAggregate['sample'][number][] = [];
  const sampleCounts: Record<ApprovalBulkExecutionAggregate['sample'][number]['outcome'], number> =
    {
      pending: 0,
      executing: 0,
      succeeded: 0,
      failed: 0,
      stale: 0,
      skipped: 0,
    };
  for (const raw of value.sample) {
    if (!isRecord(raw) || raw.entity !== value.entity || typeof raw.outcome !== 'string')
      return null;
    if (!['pending', 'executing', 'succeeded', 'failed', 'stale', 'skipped'].includes(raw.outcome))
      return null;
    const outcome = raw.outcome as ApprovalBulkExecutionAggregate['sample'][number]['outcome'];
    sampleCounts[outcome] += 1;
    sample.push({
      entity: value.entity,
      outcome,
    });
  }
  if (
    sampleCounts.succeeded > succeeded ||
    sampleCounts.failed > failed ||
    sampleCounts.stale > stale ||
    sampleCounts.skipped > skipped ||
    sampleCounts.pending + sampleCounts.executing > unfinished ||
    (unfinished === 0 && (sampleCounts.pending > 0 || sampleCounts.executing > 0))
  ) {
    return null;
  }
  return {
    version: 'partial_durable_v1',
    entity: value.entity,
    total,
    succeeded,
    failed,
    stale,
    skipped,
    unfinished,
    sample,
  };
}

export function parseApprovalCardEntry(message: Message, t: Translate): ApprovalCardEntry | null {
  if (message.role !== 'tool') {
    return null;
  }

  const record = parseJsonRecord(message.content);
  if (!record) {
    return null;
  }

  if (
    typeof record.approvalId !== 'string' ||
    typeof record.kind !== 'string' ||
    typeof record.status !== 'string' ||
    typeof record.toolName !== 'string' ||
    typeof record.expiresAt !== 'string' ||
    !isRecord(record.args)
  ) {
    return null;
  }

  if (
    !['tool_call', 'scenario_step', 'contact_delete', 'deal_delete'].includes(record.kind) ||
    !['pending', 'approved', 'rejected', 'expired', 'cancelled'].includes(record.status) ||
    Number.isNaN(Date.parse(record.expiresAt))
  ) {
    return null;
  }

  const executionOutcome = parseExecutionOutcome(record.executionOutcome);
  const consistency = parseApprovalConsistencyWarnings(record.consistency);
  const executionResult = (() => {
    if (!isRecord(record.executionResult)) return null;
    const r = record.executionResult;
    if (
      typeof r.taskId === 'string' &&
      typeof r.title === 'string' &&
      typeof r.updatedAt === 'string'
    ) {
      return { taskId: r.taskId, title: r.title, updatedAt: r.updatedAt };
    }
    if (typeof r.contactId === 'string') {
      return {
        contactId: r.contactId,
        ...(typeof r.firstName === 'string' ? { firstName: r.firstName } : {}),
        ...(typeof r.lastName === 'string' ? { lastName: r.lastName } : {}),
        ...(typeof r.updatedAt === 'string' ? { updatedAt: r.updatedAt } : {}),
      };
    }
    if (typeof r.dealId === 'string') {
      return {
        dealId: r.dealId,
        ...(typeof r.title === 'string' ? { title: r.title } : {}),
        ...(typeof r.updatedAt === 'string' ? { updatedAt: r.updatedAt } : {}),
      };
    }
    return null;
  })() as ApprovalExecutionResult | null;
  const bulkExecutionAggregate = parseBulkExecutionAggregate(record.bulkExecutionAggregate);
  const presentation = isApprovalPresentationV1(record.presentation) ? record.presentation : null;
  const legacyCopy =
    presentation === null ? canonicalApprovalCopy(record.toolName, record.args, t) : null;
  return {
    approval: {
      id: record.approvalId,
      kind: record.kind as ApprovalItem['kind'],
      status: record.status as ApprovalItem['status'],
      payload: {
        toolName: record.toolName,
        args: record.args,
        ...(isRecord(record.preview) ? { preview: record.preview } : {}),
        ...(consistency === null ? {} : { consistency }),
      },
      expiresAt: record.expiresAt,
      ...(executionOutcome === undefined ? {} : { executionOutcome }),
      ...(executionResult === null ? {} : { executionResult }),
      ...(bulkExecutionAggregate === null ? {} : { bulkExecutionAggregate }),
      ...(presentation === null ? {} : { presentation }),
    },
    summary: presentation
      ? summarizeApprovalPresentation(presentation).action
      : (legacyCopy?.summary ?? t('crm.approvals.detail.review')),
    diff: presentation
      ? summarizeApprovalPresentation(presentation)
          .details.map((detail) => `${detail.label}: ${detail.value}`)
          .join(' · ') || t('crm.approvals.detail.review')
      : t('crm.approvals.detail.review'),
  };
}

function isDealPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'deal' }> {
  return (
    payload.type === 'deal' &&
    isRecord(payload.deal) &&
    typeof payload.deal.id === 'string' &&
    typeof payload.deal.title === 'string'
  );
}

function isContactPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'contact' }> {
  return (
    payload.type === 'contact' &&
    isRecord(payload.contact) &&
    typeof payload.contact.id === 'string' &&
    typeof payload.contact.name === 'string'
  );
}

function isDealSummaryPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'deal_summary' }> {
  return (
    payload.type === 'deal_summary' &&
    isRecord(payload.summary) &&
    isRecord(payload.summary.deal) &&
    typeof payload.summary.deal.id === 'string' &&
    typeof payload.summary.deal.title === 'string'
  );
}

function isDealCockpitPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'deal_cockpit' }> {
  return (
    payload.type === 'deal_cockpit' &&
    isRecord(payload.cockpit) &&
    isRecord(payload.cockpit.deal) &&
    typeof payload.cockpit.deal.id === 'string' &&
    typeof payload.cockpit.deal.title === 'string' &&
    isRecord(payload.cockpit.health) &&
    typeof payload.cockpit.health.score === 'number'
  );
}

function isDealActionPlanPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'deal_action_plan' }> {
  return (
    payload.type === 'deal_action_plan' &&
    isRecord(payload.plan) &&
    isRecord(payload.plan.deal) &&
    typeof payload.plan.deal.id === 'string' &&
    typeof payload.plan.deal.title === 'string' &&
    Array.isArray(payload.plan.actions) &&
    isRecord(payload.plan.context)
  );
}

function isDealRiskPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'deal_risk' }> {
  return (
    payload.type === 'deal_risk' &&
    isRecord(payload.risk) &&
    isRecord(payload.risk.deal) &&
    typeof payload.risk.deal.id === 'string' &&
    typeof payload.risk.deal.title === 'string' &&
    ['low', 'medium', 'high'].includes(String(payload.risk.level)) &&
    Array.isArray(payload.risk.signals) &&
    isRecord(payload.risk.recommendation)
  );
}

function isMeetingOutcomePayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'meeting_outcome' }> {
  return (
    payload.type === 'meeting_outcome' &&
    isRecord(payload.meeting) &&
    isRecord(payload.meeting.deal) &&
    typeof payload.meeting.deal.id === 'string' &&
    typeof payload.meeting.deal.title === 'string' &&
    ['ready', 'no_note', 'too_long', 'analysis_unavailable'].includes(
      String(payload.meeting.status),
    )
  );
}

function isSalesHealthPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'sales_health' }> {
  if (payload.type !== 'sales_health' || !isRecord(payload.overview)) return false;
  // Mirrors the kit runtime guard: counters are finite non-negative integers
  // that add up, and the three sections exist. A malformed projection must not
  // render as an empty-but-valid funnel — it falls back to plain text.
  const overview = payload.overview as unknown as Record<string, unknown>;
  if (overview['mode'] !== 'sales_health') return false;
  const counters = [
    overview['activeDealCount'],
    overview['healthyCount'],
    overview['attentionCount'],
    overview['criticalCount'],
  ];
  const isNonNegativeInteger = (value: unknown): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0;
  if (!counters.every(isNonNegativeInteger)) return false;
  if (
    (overview['healthyCount'] as number) +
      (overview['attentionCount'] as number) +
      (overview['criticalCount'] as number) !==
    (overview['activeDealCount'] as number)
  ) {
    return false;
  }
  return (
    Array.isArray(overview['totalsByCurrency']) &&
    Array.isArray(overview['stages']) &&
    Array.isArray(overview['topDeals'])
  );
}

function isEmailDraftPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'email_draft' }> {
  return (
    payload.type === 'email_draft' &&
    isRecord(payload.draft) &&
    typeof payload.draft.subject === 'string' &&
    typeof payload.draft.body === 'string'
  );
}

function isMemoryPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'memory' }> {
  return (
    payload.type === 'memory' &&
    isRecord(payload.memory) &&
    typeof payload.memory.valueText === 'string'
  );
}

interface HotLeadItem {
  dealId: string;
  title: string;
  stage: string;
  amount: number;
  contactName: string | null;
  reason: string | null;
  score: number;
}

function hasHotLeadsShape(payload: StructuredMessagePayload): boolean {
  if (payload.type !== ('hot_leads' as string)) return false;
  const maybe = payload as unknown as { leads: unknown[] };
  if (!Array.isArray(maybe.leads)) return false;
  return maybe.leads.every(
    (lead) =>
      isRecord(lead) &&
      typeof lead.dealId === 'string' &&
      typeof lead.title === 'string' &&
      typeof lead.stage === 'string',
  );
}

function isScenarioPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'scenario' }> {
  return (
    payload.type === 'scenario' &&
    isRecord(payload.scenario) &&
    typeof payload.scenario.name === 'string'
  );
}

function isViewPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'view' }> {
  return (
    payload.type === 'view' &&
    isRecord(payload.view) &&
    isRecord(payload.view.spec) &&
    isRecord(payload.view.result) &&
    // Минимальная проверка формы данных: без блоков рендереру нечего рисовать.
    Array.isArray((payload.view.result as Record<string, unknown>).blocks)
  );
}

function isAddDealNoteConfirmationPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'add_deal_note_confirmation' }> {
  return (
    payload.type === 'add_deal_note_confirmation' &&
    isRecord(payload.note) &&
    typeof payload.note.dealId === 'string' &&
    typeof payload.note.dealTitle === 'string' &&
    typeof payload.note.body === 'string' &&
    isRecord(payload.action) &&
    payload.action.type === 'confirm_add_deal_note' &&
    typeof payload.action.label === 'string' &&
    isRecord(payload.action.payload) &&
    typeof payload.action.payload.dealId === 'string' &&
    typeof payload.action.payload.body === 'string'
  );
}

/**
 * B3: runtime guard for the touch-strategy payload. The kit union already
 * types the record; the checks mirror the chat-service projection so a
 * malformed row (empty steps, missing diagnosis) falls back to plain text
 * instead of rendering an empty-but-valid strategy card.
 */
function isTouchStrategyPayload(
  payload: StructuredMessagePayload,
): payload is TouchStrategyPayload {
  return (
    payload.type === 'crm_touch_strategy_v1' &&
    typeof payload.dealId === 'string' &&
    payload.dealId !== '' &&
    typeof payload.diagnosis === 'string' &&
    payload.diagnosis !== '' &&
    Array.isArray(payload.steps) &&
    payload.steps.length > 0 &&
    payload.steps.every(
      (step) =>
        typeof step.dayOffset === 'number' &&
        Number.isInteger(step.dayOffset) &&
        typeof step.channel === 'string' &&
        ['email', 'call', 'meeting', 'telegram'].includes(step.channel) &&
        typeof step.goal === 'string' &&
        step.goal !== '',
    ) &&
    Array.isArray(payload.evidenceSignalCodes) &&
    evidenceSignalCodesAreStrings(payload.evidenceSignalCodes) &&
    typeof payload.sourcePreset === 'string' &&
    ['proposal_silent', 'stale_silence', 'discovery', 'no_next_step'].includes(payload.sourcePreset)
  );
}

function evidenceSignalCodesAreStrings(value: unknown[]): boolean {
  return value.every((code) => typeof code === 'string' && code !== '');
}

/**
 * B2: runtime guard for the qualification proposal payload. The kit union
 * already types the record; the checks mirror the chat-service projection so
 * a malformed row (empty items, unknown field names) falls back to plain text
 * instead of rendering an empty-but-valid checklist card.
 */
function isQualificationProposalPayload(
  payload: StructuredMessagePayload,
): payload is QualificationProposalPayload {
  return (
    payload.type === 'crm_qualification_proposal_v1' &&
    typeof payload.dealId === 'string' &&
    payload.dealId !== '' &&
    Array.isArray(payload.items) &&
    payload.items.length > 0 &&
    payload.items.length <= 5 &&
    payload.items.every(
      (item) =>
        typeof item.field === 'string' &&
        ['need', 'budget', 'decisionMaker', 'decisionDeadline', 'selectionCriteria'].includes(
          item.field,
        ) &&
        typeof item.status === 'string' &&
        ['unknown', 'confirmed', 'missing'].includes(item.status),
    )
  );
}

/** Compact failure codes the propose_touch_strategy tool can return. */
const TOUCH_STRATEGY_FAILURE_CODES: ReadonlySet<string> = new Set([
  'touch_strategy_guard',
  'touch_strategy_llm',
  'touch_strategy_persist',
]);

/** Compact failure codes the extract_deal_qualification tool can return. */
const QUALIFICATION_FAILURE_CODES: ReadonlySet<string> = new Set([
  'qualification_llm',
  'qualification_no_deal',
]);

/**
 * B3: a failed strategy carries no structured payload — the tool result is
 * the `{ok:false, code}` envelope. Rendered as a compact localized notice
 * instead of the raw JSON whenever such a tool message reaches the renderer.
 */
function touchStrategyFailureCode(message: Message): TouchStrategyFailureCode | undefined {
  if (message.role !== 'tool') return undefined;
  const record = parseJsonRecord(message.content);
  if (record === null || record['ok'] !== false) return undefined;
  const code = record['code'];
  return typeof code === 'string' && TOUCH_STRATEGY_FAILURE_CODES.has(code)
    ? (code as TouchStrategyFailureCode)
    : undefined;
}

/**
 * B2: a failed qualification extraction carries no structured payload — the
 * tool result is the `{ok:false, code}` envelope. Rendered as a compact
 * localized notice instead of the raw JSON.
 */
function qualificationFailureCode(message: Message): QualificationFailureCode | undefined {
  if (message.role !== 'tool') return undefined;
  const record = parseJsonRecord(message.content);
  if (record === null || record['ok'] !== false) return undefined;
  const code = record['code'];
  return typeof code === 'string' && QUALIFICATION_FAILURE_CODES.has(code)
    ? (code as QualificationFailureCode)
    : undefined;
}

function isArtifactPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'artifact' }> {
  return (
    payload.type === 'artifact' &&
    isRecord(payload.artifact) &&
    typeof payload.artifact.id === 'string' &&
    typeof payload.artifact.title === 'string' &&
    typeof payload.artifact.content === 'string'
  );
}

function isModelUpgradeOfferPayload(
  payload: StructuredMessagePayload,
): payload is Extract<StructuredMessagePayload, { type: 'model_upgrade_offer' }> {
  return (
    payload.type === 'model_upgrade_offer' &&
    typeof payload.currentModel === 'string' &&
    typeof payload.fallbackModel === 'string' &&
    typeof payload.requestedLevel === 'string' &&
    (payload.currentCertifiedThrough === null ||
      typeof payload.currentCertifiedThrough === 'string') &&
    payload.action === 'request_model_upgrade' &&
    typeof payload.title === 'string' &&
    typeof payload.message === 'string' &&
    typeof payload.confirmLabel === 'string'
  );
}

function renderFallback(content: string): ReactElement {
  return <span className="ack-chat-panel__content">{content}</span>;
}

function HotLeadsSnippet({
  leads,
  onOpenEntity,
}: {
  leads: HotLeadItem[];
  onOpenEntity?: ((type: 'deal' | 'contact', id: string, title?: string) => void) | undefined;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <header className="border-b border-gray-100 px-4 py-3">
        <h3 className="m-0 text-sm font-semibold text-gray-900">{t('crm.hot_leads.title')}</h3>
      </header>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-xs font-medium text-gray-500">
            <th className="px-4 py-2 text-left">{t('crm.hot_leads.table_header.deal')}</th>
            <th className="px-4 py-2 text-left">{t('crm.hot_leads.table_header.contact')}</th>
            <th className="px-4 py-2 text-left">{t('crm.hot_leads.table_header.stage')}</th>
            <th className="px-4 py-2 text-right">{t('crm.hot_leads.table_header.amount')}</th>
            <th className="px-4 py-2 text-left">{t('crm.hot_leads.table_header.reason')}</th>
            <th className="px-4 py-2 text-right">{t('crm.hot_leads.table_header.score')}</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr
              key={lead.dealId}
              className="border-b border-gray-50 last:border-0 hover:bg-gray-50"
            >
              <td className="px-4 py-2">
                <button
                  type="button"
                  className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 hover:text-blue-800"
                  onClick={() => onOpenEntity?.('deal', lead.dealId, lead.title)}
                >
                  {lead.title}
                </button>
              </td>
              <td className="px-4 py-2 text-gray-700">{lead.contactName ?? '—'}</td>
              <td className="px-4 py-2">
                <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  {lead.stage}
                </span>
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                {lead.amount > 0 ? lead.amount.toLocaleString() : '—'}
              </td>
              <td className="px-4 py-2 text-gray-600">{lead.reason ?? '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                    lead.score >= 50
                      ? 'bg-red-100 text-red-700'
                      : lead.score >= 30
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {lead.score}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function StructuredMessageRenderer({
  message,
  artifactDealId,
  onSendEmailDraft,
  sendEmailDraftDisabled = false,
  onPrepareDealFollowUp,
  prepareDealFollowUpDisabled = false,
  onProposeTouchStrategy,
  onCreateDealTasks,
  createDealTasksDisabled = false,
  onCreateMeetingTasks,
  createMeetingTasksDisabled = false,
  onPrepareMeetingFollowUp,
  prepareMeetingFollowUpDisabled = false,
  onOpenCanvas,
  onOpenEntity,
  onExecuteScenario,
  onRequestModelUpgrade,
  onApprovalDecisionApplied,
  onDecideActionPlanItem,
  onRequestActionPlanUndo,
  onRequestActionPlanRetry,
  compact = false,
}: StructuredMessageRendererProps): ReactElement {
  const { t } = useTranslation();
  const approvalEntry = parseApprovalCardEntry(message, t);
  if (approvalEntry) {
    return (
      <AiApprovalCard
        approval={approvalEntry.approval}
        summary={approvalEntry.summary}
        diff={approvalEntry.diff}
        onExecuteCommand={
          onExecuteScenario ? (command) => onExecuteScenario({ prompt: '', command }) : undefined
        }
        onOpenEntity={onOpenEntity}
        onDecisionApplied={onApprovalDecisionApplied}
      />
    );
  }

  const payload = message.structuredPayload;
  if (!payload || !isRecord(payload)) {
    if (message.role === 'assistant') {
      const extracted = extractArtifactFromMarkdown(message.content);
      if (extracted) {
        if (extracted.type === 'document') {
          const headingIndex = message.content.indexOf('# ');
          const intro = headingIndex > 0 ? message.content.slice(0, headingIndex).trim() : null;
          return (
            <div className="space-y-2">
              {intro ? renderFallback(intro) : null}
              <ArtifactSnippet
                artifact={extracted}
                sourceMessageId={message.id}
                artifactIndex={0}
                dealId={artifactDealId}
                onExecuteCommand={(command) => onExecuteScenario?.({ prompt: '', command })}
                onOpenCanvas={onOpenCanvas}
              />
            </div>
          );
        }
        return (
          <div className="space-y-2">
            {renderFallback(message.content)}
            <ArtifactSnippet
              artifact={extracted}
              sourceMessageId={message.id}
              artifactIndex={0}
              dealId={artifactDealId}
              onExecuteCommand={(command) => onExecuteScenario?.({ prompt: '', command })}
              onOpenCanvas={onOpenCanvas}
            />
          </div>
        );
      }
    }
    return renderFallback(message.content);
  }

  if (isChatFileAttachmentPayload(payload)) {
    // The user message keeps its own text: the chip only marks the file
    // provenance of this turn (PR 5).
    return (
      <div className="space-y-1.5">
        {message.content.trim() !== '' ? renderFallback(message.content) : null}
        <ChatFileAttachmentSnippet fileName={payload.fileName} format={payload.format} />
      </div>
    );
  }

  if (isChatFilePreviewPayload(payload)) {
    return <ChatFilePreviewSnippet preview={payload} />;
  }

  if (isArtifactPayload(payload)) {
    return (
      <ArtifactSnippet
        artifact={payload.artifact}
        sourceMessageId={message.id}
        artifactIndex={0}
        dealId={artifactDealId}
        onExecuteCommand={(command) => onExecuteScenario?.({ prompt: '', command })}
        onOpenCanvas={onOpenCanvas}
      />
    );
  }

  if (isModelUpgradeOfferPayload(payload)) {
    return (
      <section className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <h3 className="m-0 text-sm font-semibold text-amber-950">{payload.title}</h3>
        <p className="mt-1 text-sm text-amber-900">{payload.message}</p>
        <div className="mt-2 text-xs text-amber-800">
          {payload.currentModel} → {payload.fallbackModel} · {payload.requestedLevel}
        </div>
        <button
          type="button"
          className="mt-3 rounded-md bg-amber-700 px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          disabled={onRequestModelUpgrade === undefined}
          onClick={() => onRequestModelUpgrade?.(payload)}
        >
          {payload.confirmLabel}
        </button>
      </section>
    );
  }

  if (isCrmListPayload(payload)) {
    return (
      <CrmStructuredList
        payload={payload}
        compact={compact}
        onNextPage={
          payload.list.hasMore && onExecuteScenario !== undefined
            ? () =>
                onExecuteScenario({
                  prompt: t('crm.chat.crm_list.next_page'),
                  command: commandRequest('crmListNextPage', { sourceMessageId: message.id }),
                  contextRefs: [
                    {
                      type: 'crm_list',
                      id: message.id,
                      label: t('crm.chat.crm_list.next_page_source'),
                    },
                    {
                      type: 'command',
                      id: RESULT_ACTION_COMMAND_IDS.nextPage,
                      label: t('crm.chat.crm_list.next_page'),
                    },
                  ],
                })
            : undefined
        }
      />
    );
  }

  if (isCrmActionPlanPayload(payload)) {
    return (
      <CrmActionPlanCard
        plan={payload.plan}
        onDecideActionPlanItem={onDecideActionPlanItem}
        onRequestActionPlanUndo={onRequestActionPlanUndo}
        onRequestActionPlanRetry={onRequestActionPlanRetry}
        onOpenEntity={onOpenEntity}
      />
    );
  }

  if (isDealPayload(payload)) {
    return <DealSnippet deal={payload.deal} onOpenEntity={onOpenEntity} />;
  }
  if (payload.type === 'deal_call_preparation')
    return <DealCallPreparationSnippet preparation={payload.preparation} />;
  if (isMeetingOutcomePayload(payload)) {
    return (
      <MeetingOutcomeSnippet
        meeting={payload.meeting}
        onCreateTasks={onCreateMeetingTasks}
        createTasksDisabled={createMeetingTasksDisabled}
        onPrepareFollowUp={onPrepareMeetingFollowUp}
        prepareFollowUpDisabled={prepareMeetingFollowUpDisabled}
      />
    );
  }
  if (isSalesHealthPayload(payload)) {
    return <SalesHealthSnippet overview={payload.overview} />;
  }
  if (isDealSummaryPayload(payload)) {
    return <DealSummarySnippet summary={payload.summary} onOpenEntity={onOpenEntity} />;
  }
  if (isDealCockpitPayload(payload)) {
    return (
      <DealCockpitSnippet
        cockpit={payload.cockpit}
        onProposeTouchStrategy={onProposeTouchStrategy}
      />
    );
  }
  if (isDealActionPlanPayload(payload)) {
    return (
      <DealActionPlanSnippet
        plan={payload.plan}
        onCreateTasks={
          onCreateDealTasks === undefined
            ? undefined
            : (actions) => onCreateDealTasks(payload.plan, actions)
        }
        createTasksDisabled={createDealTasksDisabled}
      />
    );
  }
  if (isDealRiskPayload(payload)) {
    return (
      <DealRiskSnippet
        risk={payload.risk}
        onPrepareFollowUp={onPrepareDealFollowUp}
        prepareFollowUpDisabled={prepareDealFollowUpDisabled}
        onOpenEntity={onOpenEntity}
        compact={compact}
      />
    );
  }
  if (hasHotLeadsShape(payload)) {
    return (
      <HotLeadsSnippet
        leads={(payload as unknown as { leads: HotLeadItem[] }).leads}
        onOpenEntity={onOpenEntity}
      />
    );
  }

  if (payload.type === 'manager_briefing') {
    return (
      <ManagerBriefingSnippet
        briefing={payload.briefing}
        sourceMessageId={message.id}
        onOpenEntity={onOpenEntity}
        onExecuteScenario={onExecuteScenario}
      />
    );
  }
  if (isContactPayload(payload)) {
    return <ContactSnippet contact={payload.contact} onOpenEntity={onOpenEntity} />;
  }
  if (isEmailDraftPayload(payload)) {
    return (
      <EmailDraftSnippet
        draft={payload.draft}
        onSendForApproval={
          onSendEmailDraft === undefined ? undefined : () => onSendEmailDraft(payload.draft)
        }
        sendDisabled={sendEmailDraftDisabled}
      />
    );
  }
  if (isMemoryPayload(payload)) {
    return <MemorySnippet memory={payload.memory} />;
  }
  if (isScenarioPayload(payload)) {
    return <ScenarioSnippet scenario={payload.scenario} />;
  }
  if (isViewPayload(payload)) {
    return <ViewSnippet view={payload.view} />;
  }

  if (isAddDealNoteConfirmationPayload(payload)) {
    const actionPayload = payload.action.payload as unknown;
    const persistedApprovalState = isRecord(actionPayload) ? actionPayload : null;
    return (
      <AddDealNoteConfirmationSnippet
        note={payload.note}
        action={payload.action}
        approvalStatus={parseApprovalStatus(persistedApprovalState?.approvalStatus)}
        executionOutcome={parseExecutionOutcome(persistedApprovalState?.executionOutcome)}
      />
    );
  }

  if (isTouchStrategyPayload(payload)) {
    return (
      <TouchStrategySnippet strategy={payload} onDecisionApplied={onApprovalDecisionApplied} />
    );
  }

  if (isQualificationProposalPayload(payload)) {
    return (
      <QualificationProposalSnippet
        proposal={payload}
        onDecisionApplied={onApprovalDecisionApplied}
      />
    );
  }

  const touchStrategyFailure = touchStrategyFailureCode(message);
  if (touchStrategyFailure !== undefined) {
    return <TouchStrategySnippet failureCode={touchStrategyFailure} />;
  }

  const qualificationFailure = qualificationFailureCode(message);
  if (qualificationFailure !== undefined) {
    return <QualificationProposalSnippet failureCode={qualificationFailure} />;
  }

  return renderFallback(message.content);
}
